'use client';

// ---------------------------------------------------------------------------
// useMusicLibrary — the stock catalogue, plus this device's own files
// ---------------------------------------------------------------------------
//
// Two sources that the player treats as one list. Stock tracks come from the
// server; local tracks come out of this browser's IndexedDB. They are fetched
// separately because they fail separately: the network can be down while local
// files are fine, and IndexedDB can be blocked while the catalogue loads.
//
// Both feed the store's `tracks` map, which is what the queue resolves ids
// against. Without that the queue would hold ids nothing could describe.
// ---------------------------------------------------------------------------

import { useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/core/lib/api-client';
import {
  type LocalTrackRecord,
  deleteLocalTrack,
  getAllLocalTracks,
  isLocalFilesSupported,
  putLocalTrack,
} from '@/core/lib/music-db';

import { sweepPermissions, titleFromFilename } from '../lib/local-verify';
import type { LocalTrack, StockTrack, Track } from '../lib/track';
import { useMusicStore } from '../stores/music.store';

interface LibraryResponse {
  data: {
    id: string;
    title: string;
    artist: string | null;
    album: string | null;
    genre: string | null;
    durationSec: number;
    mimeType: string;
    sizeBytes: number;
  }[];
  meta: { total: number; genres: string[] };
}

function toStockTrack(row: LibraryResponse['data'][number]): StockTrack {
  return {
    source: 'STOCK',
    id: row.id,
    title: row.title,
    artist: row.artist,
    album: row.album,
    genre: row.genre,
    durationSec: row.durationSec,
    mimeType: row.mimeType,
  };
}

function toLocalTrack(record: LocalTrackRecord): LocalTrack {
  return {
    source: 'LOCAL',
    id: record.id,
    title: record.title,
    artist: record.artist,
    durationSec: record.durationSec,
    sizeBytes: record.sizeBytes,
    // Not persisted, so it starts unknown and the sweep fills it in. Treating it
    // as playable meanwhile is deliberate — see isPlayable in lib/track.ts.
    status: 'unknown',
  };
}

/**
 * Read a file's duration by loading it into a throwaway audio element.
 *
 * The only way to get it in the browser without an audio-decoding dependency.
 * Resolves null rather than rejecting: an unreadable duration is cosmetic, and
 * failing the whole add over it would be worse than showing '--:--'.
 */
function readDuration(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const probe = document.createElement('audio');
    const finish = (value: number | null) => {
      probe.removeAttribute('src');
      URL.revokeObjectURL(url);
      resolve(value);
    };
    probe.addEventListener('loadedmetadata', () =>
      finish(Number.isFinite(probe.duration) && probe.duration > 0 ? Math.round(probe.duration) : null),
    );
    probe.addEventListener('error', () => finish(null));
    probe.preload = 'metadata';
    probe.src = url;
  });
}

const LOCAL_QUERY_KEY = ['music', 'local-tracks'] as const;

/** The stock catalogue. One query, shared by every caller through its key. */
function useStockLibraryQuery() {
  // No automatic refetch loop: the catalogue changes when an admin uploads,
  // which is rare, and the panel is opened on demand.
  return useQuery({
    queryKey: ['music', 'library'],
    queryFn: () => api.get<LibraryResponse>('/music/library'),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Put the stock catalogue into the player store, and nothing else.
 *
 * For the compact panel, whose play button starts the library when the queue
 * is empty: it must know the library exists without mounting the expanded
 * panel. Shares the query above, so this is never a second request.
 */
export function useStockLibraryIntoStore(): void {
  const upsertTracks = useMusicStore((s) => s.upsertTracks);
  const stockQuery = useStockLibraryQuery();
  useEffect(() => {
    const tracks = (stockQuery.data?.data ?? []).map(toStockTrack);
    if (tracks.length > 0) upsertTracks(tracks);
  }, [stockQuery.data, upsertTracks]);
}

export function useMusicLibrary() {
  const upsertTracks = useMusicStore((s) => s.upsertTracks);
  const setLocalStatus = useMusicStore((s) => s.setLocalStatus);
  const removeTrack = useMusicStore((s) => s.removeTrack);
  const queryClient = useQueryClient();

  const stockQuery = useStockLibraryQuery();

  /**
   * Local files, through React Query rather than useState + useEffect.
   *
   * Not a style preference: reading IndexedDB in an effect and calling setState
   * in the async continuation trips `react-hooks/set-state-in-effect`, and
   * suppressing that would be papering over a real cascading-render risk. React
   * Query owns the async lifecycle, gives invalidation instead of a hand-rolled
   * reload, and matches how the stock catalogue above is fetched.
   *
   * The permission sweep happens inside the query so the statuses arrive with the
   * records, not one render later.
   */
  const localQuery = useQuery({
    queryKey: LOCAL_QUERY_KEY,
    // `enabled` rather than an early return: on Firefox and Safari there is
    // nothing to read, and a query that resolves empty is honest about that.
    enabled: isLocalFilesSupported(),
    staleTime: 30 * 1000,
    queryFn: async () => {
      const records = await getAllLocalTracks();
      const statuses = await sweepPermissions(records);
      return { tracks: records.map(toLocalTrack), statuses };
    },
  });

  const stockTracks: StockTrack[] = (stockQuery.data?.data ?? []).map(toStockTrack);
  const genres: string[] = stockQuery.data?.meta?.genres ?? [];
  const localTracks: LocalTrack[] = localQuery.data?.tracks ?? [];

  // Push both into the zustand store, which is what the queue resolves ids
  // against. Writing to an external store from an effect is the sanctioned use of
  // one — it is React state that must not be set here, not external state.
  useEffect(() => {
    if (stockTracks.length > 0) upsertTracks(stockTracks);
    // Keyed on the query result, not the derived array, which is a new reference
    // every render and would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockQuery.data, upsertTracks]);

  useEffect(() => {
    const result = localQuery.data;
    if (!result) return;
    upsertTracks(result.tracks);
    for (const [id, status] of result.statuses) setLocalStatus(id, status);
  }, [localQuery.data, upsertTracks, setLocalStatus]);

  const reloadLocal = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: LOCAL_QUERY_KEY });
  }, [queryClient]);

  /**
   * Ask for files and remember them.
   *
   * MUST be called from a user gesture — `showOpenFilePicker` is rejected
   * otherwise, which is why this is only ever wired to a click.
   */
  const addLocalFiles = useCallback(async (): Promise<number> => {
    if (typeof window === 'undefined' || !window.showOpenFilePicker) return 0;

    let handles;
    try {
      handles = await window.showOpenFilePicker({
        multiple: true,
        excludeAcceptAllOption: false,
        types: [
          {
            description: 'Audio',
            accept: {
              'audio/*': ['.mp3', '.m4a', '.aac', '.ogg', '.oga', '.wav', '.flac'],
            },
          },
        ],
      });
    } catch {
      // The user cancelled the dialog. Not an error, and must not raise a toast.
      return 0;
    }

    let added = 0;
    for (const handle of handles) {
      try {
        const file = await handle.getFile();
        const durationSec = await readDuration(file);
        const record: LocalTrackRecord = {
          id: `local:${crypto.randomUUID()}`,
          handle,
          title: titleFromFilename(handle.name),
          artist: null,
          durationSec,
          sizeBytes: file.size,
          lastModified: file.lastModified,
          addedAt: Date.now(),
          lastVerifiedAt: Date.now(),
        };
        await putLocalTrack(record);
        added += 1;
      } catch {
        // One unreadable file must not abandon the rest of the selection.
      }
    }

    await reloadLocal();
    return added;
  }, [reloadLocal]);

  const forgetLocalFile = useCallback(
    async (id: string) => {
      try {
        await deleteLocalTrack(id);
      } finally {
        // Drop it from the store even if the delete failed, so the UI does not
        // keep offering a track the user asked to remove.
        removeTrack(id);
        await reloadLocal();
      }
    },
    [removeTrack, reloadLocal],
  );

  const allTracks: Track[] = [...stockTracks, ...localTracks];

  return {
    stockTracks,
    localTracks,
    allTracks,
    genres,
    isLoading: stockQuery.isLoading,
    error: stockQuery.error
      ? 'The music library could not be loaded.'
      : localQuery.error
        ? 'Saved files could not be read from this browser.'
        : null,
    refetchStock: stockQuery.refetch,
    localFilesSupported: isLocalFilesSupported(),
    addLocalFiles,
    forgetLocalFile,
    reloadLocal,
  };
}
